// VisualRegressions — v161.0
//
// Per-build panel that surfaces visual-regression flags from the
// auto-pilot's screenshot-hash pipeline. Renders SILENT (no DOM) when
// there are zero regressions or when the admin endpoint returns 403
// (non-admin viewer). When there are regressions, renders a compact
// red-bordered alert with collapsible per-step detail.
//
// Visual regression = a step's average-hash differs from the same
// step's prior-build hash by more than the threshold (default 16/256
// bits flipped, ~6% of pixels). Caught: layout shifts, color
// regressions, missing assets, text overflow, dark-mode breakage,
// asset-pipeline corruption — none of which the AX-tree-only
// predicates would notice.

import { useEffect, useState } from 'react';
import { admin } from '../lib/api';

export default function VisualRegressions({ buildId }) {
  const [regressions, setRegressions] = useState(null); // null = loading, [] = none, [...] = found
  const [summary, setSummary] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!buildId) return;
    admin.visualRegressionsForBuild(buildId, 50)
      .then(r => {
        if (cancelled) return;
        setRegressions(r.regressions || []);
        setSummary(r.summary || null);
      })
      .catch(() => {
        // 403 (non-admin) or network error — silent no-render. Non-admins
        // shouldn't see admin tiles flicker into existence.
        if (!cancelled) setHidden(true);
      });
    return () => { cancelled = true; };
  }, [buildId]);

  if (hidden) return null;
  if (regressions === null) return null; // still loading
  if (regressions.length === 0) return null; // healthy build, no nag

  return (
    <div style={{
      marginTop: 12,
      padding: 12,
      borderRadius: 10,
      background: 'rgba(239, 68, 68, 0.08)',
      border: '1px solid rgba(239, 68, 68, 0.30)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', cursor: 'pointer' }}
           onClick={() => setExpanded(e => !e)}>
        <div>
          <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 13 }}>
            ⚠️ Visual regressions detected
          </span>
          <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.75 }}>
            {regressions.length} step{regressions.length === 1 ? '' : 's'} differ from prior build
          </span>
        </div>
        <span style={{ fontSize: 11, opacity: 0.55 }}>
          {expanded ? '▼ hide' : '▶ show'}
        </span>
      </div>

      {expanded && (
        <div style={{ marginTop: 10, paddingTop: 10,
                        borderTop: '1px solid rgba(239, 68, 68, 0.15)' }}>
          {regressions.slice(0, 20).map(r => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between',
                                        alignItems: 'baseline', gap: 8,
                                        padding: '4px 0', fontSize: 12 }}>
              <span style={{ fontVariantNumeric: 'tabular-nums', flex: 1, minWidth: 0 }}>
                <code style={{ opacity: 0.7 }}>{r.step_id}</code>
                <span style={{ marginLeft: 8, opacity: 0.55 }}>
                  ({r.device_class})
                </span>
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85, whiteSpace: 'nowrap' }}>
                {r.distance_to_baseline}/256 bits
                {r.baseline_build_id && (
                  <span style={{ marginLeft: 8, opacity: 0.55 }}>
                    vs <code>{r.baseline_build_id.slice(0, 8)}</code>
                  </span>
                )}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // v161.1: admin marks an intentional regression as
                  // expected. Confirm so a misclick doesn't permanently
                  // hide a real bug. Dismissed rows stop re-flagging
                  // future builds against the pre-change baseline.
                  const reason = prompt(
                    `Mark this regression as intentional?\n\n` +
                    `Step: ${r.step_id}\nDistance: ${r.distance_to_baseline}/256 bits\n\n` +
                    `Optional: why is this expected? (visible in audit log)`,
                    ''
                  );
                  if (reason === null) return; // cancelled
                  admin.dismissVisualRegression(r.id, reason).then(() => {
                    // Optimistic update: drop from local list.
                    setRegressions(prev => prev.filter(x => x.id !== r.id));
                  }).catch(() => alert('Failed to dismiss — try again'));
                }}
                style={{
                  fontSize: 11, padding: '2px 8px', border: 'none',
                  borderRadius: 4, cursor: 'pointer',
                  background: 'rgba(255,255,255,0.08)',
                  color: 'inherit', opacity: 0.7
                }}
                title="Mark as intentional (this regression is expected)"
              >
                expected ✓
              </button>
            </div>
          ))}
          {regressions.length > 20 && (
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>
              … and {regressions.length - 20} more
            </div>
          )}
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 10 }}>
            Hash distance is bits flipped on a 256-bit avg-hash of the screenshot.
            Threshold defaults to 16 (~6% of pixels). Single-pixel font-hint drift
            usually scores below 8.
          </div>
        </div>
      )}
    </div>
  );
}
